<template>
  <zm-dialog
    :visible.sync="visible"
    :append-to-body="true"
    aria-label="port number validate dialog"
    class="port-number-validate opc-full-zm-dialog"
    ref="portValidateDialog"
    width="530px"
  >
    <h1 class="port-number-validate__title" slot="title">
      {{ $t('billing.pbx.port_number.title') }}
      <zm-tooltip v-if="$context.is.desktop || $context.is.tablet" aria-label="learn more about validate port number" placement="bottom" tab-to-leave>
        <p slot="content" class="port-number-guarantee-tips">{{ $t('billing.pbx.port_number.guarantee_tips') }}</p>
        <i class="zm-icon-warning-outline" role="button"></i>
      </zm-tooltip>
    </h1>
    <h2 v-if="!($context.is.desktop || $context.is.tablet)" class="port-number-validate__guarantee-tips">{{ $t('billing.pbx.port_number.guarantee_tips') }}</h2>
    <div class="port-number-validate__content">
      <zm-form inline :class="{'port-number-validate__error-form': errorMessage }">
        <zm-form-item :error="errorMessage">
          <zm-input
            size="large"
            :maxLength="14"
            :placeholder="$t('billing.pbx.port_number.placeholder')"
            :value="form.portNumber | phoneFormat"
            :disabled="loading"
            @input="changePortNumber"
            @keyup.enter.native="validate"
            class="port-number-validate__input"
          >
            <i v-if="loading" slot="suffix" class="zm-icon-loading"></i>
          </zm-input>
        </zm-form-item>
        <zm-button
          type="link"
          class="port-number-validate__btn"
          :disabled="validateDisabled"
          @click="validate"
        >
          {{ $t('billing.common.validate') }}
        </zm-button>
      </zm-form>
      <port-number-checked-list
        v-if="checkPortNumberCount > 0"
        :port-number-list="checkedNumberList"
        :portNumberShowClear="portNumberShowClear"
        @removeNumber="removeNumber"
      />
      <max-phone-number-warning key="maxNumberError" v-if="maxNumberError" />
      <zm-alert
        key="maxLicenseError"
        v-else-if="maxLicenseError"
        type="warning"
        class="port-number-validate__content--max-license-warning">
        <i class="billing-icon-warning" />
        <p v-html="$t('billing.pbx.more_than_99_license', [pbxContactSalesLink])" />
      </zm-alert>
      <zm-alert
        key="successful"
        v-if="showSuccess"
        type="success"
        v-html="successTipMsg" />
      <p v-else-if="showError"
         class="port-number-validate__content--verify-error-msg"
         v-html="$t('billing.pbx.port_number.verify_fail', [pbxContactSalesLink])" />
    </div>
    <phone-select-footer
      slot="footer"
      class="port-number-validate__footer"
      :disabled-save="disabledSave"
      :save-text="$t('common.btn_continue')"
      :cancel-text="$t('common.btn_cancel')"
      :on-cancel="onCancel"
      :on-save="onSave"
    />
  </zm-dialog>
</template>

<script>
import { uniq, isEqual } from 'lodash';
import { checkPortNumber } from '@/plans/ZOOM_PHONE/helper/validate';
import { isEmptyVal } from '@/utils';
import { formatPortNumber, parsePortNumber } from '@/plans/ZOOM_PHONE/helper/utils';
import phoneNumberApiMixin from '@/plans/ZOOM_PHONE/mixin/phone-number-api';
import PortNumberCheckedList from '@/plans/ZOOM_PHONE/component/port-number-checked-list';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import MaxPhoneNumberWarning from '@/plans/ZOOM_PHONE/component/max-phone-number-warning';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { maxSelectPhoneNumbers } from '@/plans/ZOOM_PHONE/helper/constants';
import { getPbxContactSalesLink } from '@/utils/i18n-shortcuts';
import PhoneSelectFooter from "./phone-select-footer";
import {
  computedPbxMeteredPlans,
  computedPbxUnlimitedPlans,
} from '@/common/service/computers/plans/pbx';
import { mapGetters } from 'vuex';

const blockCaseMap = {
  maxLicense: 'MAX-LICENSE-99',
  maxNumber: 'MAX-number-10',
};

export default {
  name: 'ValidatePortNumberDialog',
  components: { MaxPhoneNumberWarning, PortNumberCheckedList, PhoneSelectFooter },
  mixins: [ConfirmComponent, phoneNumberApiMixin, computedMixin],
  filters: {
    phoneFormat: parsePortNumber,
  },
  data() {
    return {
      form: {
        portNumber: '',
      },
      error: {
        frontError: '',
        backError: '',
      },
      checkedNumberList: [],
      originPortNumberList: [],
      localCount: 0,
      tollFreeCount: 0,
      portCount: 0,
      // true means phone number not required check
      // false means phone number should required check
      clearable: true,
      pbxContactSalesLink: getPbxContactSalesLink(),
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlans"]),
    maxLicenseError() {
      return this.blockValidateNumber === blockCaseMap.maxLicense;
    },
    maxNumberError() {
      return this.blockValidateNumber === blockCaseMap.maxNumber;
    },
    blockValidateNumber() {
      const otherNumbersTotal = this.localCount + this.tollFreeCount;

      // todo just port number
      const checkedNumbersCount = this.checkedNumberList?.length || 0;
      const totalCount = otherNumbersTotal + checkedNumbersCount;

      if (totalCount >= 99) {
        return blockCaseMap.maxLicense;
      } else if (checkedNumbersCount >= maxSelectPhoneNumbers) {
        return blockCaseMap.maxNumber;
      }

      return '';
    },
    errorMessage() {
      return this.error.frontError || this.error.backError;
    },
    showError() {
      return !isEmptyVal(this.form.portNumber) && this.error.backError;
    },
    showSuccess() {
      const originPortNumberList = this.originPortNumberList || [];
      const checkedNumberCount = this.checkedNumberList?.length;

      const basicValidate = checkedNumberCount > 0 && isEmptyVal(this.error.backError);

      if (originPortNumberList?.length) {
        return basicValidate && !isEqual(this.originPortNumberList, this.checkedNumberList);
      }

      return basicValidate;
    },
    validateDisabled() {
      return this.loading || isEmptyVal(this.form.portNumber) || !isEmptyVal(this.blockValidateNumber);
    },
    checkPortNumberCount() {
      return this.checkedNumberList?.length || 0;
    },
    disabledSave() {
      // &. is validating port number
      // &. have error message for validate port number
      // 1. have selected local number or toll-free number
      // 2. have selected port number is empty
      // 3. current checked port number is empty
      return this.loading
        || isEqual(this.originPortNumberList, this.checkedNumberList)
        || (isEmptyVal(this.errorMessage) === false && this.checkPortNumberCount === 0)
        || (this.clearable && this.checkPortNumberCount === 0 && this.portCount === 0);
    },
    portNumberShowClear() {
      return this.clearable ? true : (this.checkPortNumberCount === 1 ? false : true);
    },
    successTipMsg() { // just apply for US/CA region, users can only choose one type of calling plan, either unlimited or metered
      const unlimitedPlans = computedPbxUnlimitedPlans(this.getSelectedPlans);

      if (unlimitedPlans?.length) {
        return this.$t('billing.pbx.port_number.verify_successful');
      }

      const meteredPlans = computedPbxMeteredPlans(this.getSelectedPlans);

      if (meteredPlans?.length) {
        return this.$t('billing.pbx.port_number.verify_successful_metered');
      }

      return "";
    },
  },
  methods: {
    resetPortNumberForm() {
      this.form.portNumber = '';
      this.error = { frontError: '', backError: '' };
    },
    changePortNumber(value) {
      const regex = /^[\d-()\s]+$/g;

      if (isEmptyVal(value) || regex.test(value)) {
        this.form.portNumber = value;
      }

      this.error.backError = '';
      this.error.frontError = checkPortNumber(formatPortNumber(value), this.checkedNumberList, value);
    },
    async validate() {
      if (this.validateDisabled) return;

      const portNumber = this.form.portNumber;
      const errorMsg = checkPortNumber(formatPortNumber(portNumber), this.checkedNumberList, portNumber);
      this.$tracking.buyInteraction('zoom phone numbers', `${this.callingPlanName} - port your numbers - validate`);
      if (errorMsg) return;

      const phoneNumber = formatPortNumber(this.form.portNumber);
      const backMessage = await this.validatePortNumbers(phoneNumber);

      if (isEmptyVal(backMessage)) {
        this.form.portNumber = '';
        this.checkedNumberList = uniq([...this.checkedNumberList, phoneNumber]);
      }

      this.error.backError = backMessage;
    },
    removeNumber(index) {
      this.checkedNumberList.splice(index, 1);
    },
    onSave() {
      this.$tracking.buyInteraction('zoom phone numbers', `${this.callingPlanName} - port your numbers - save`);
      return this.handleSure(this.checkedNumberList);
    },
    onCancel() {
      this.$tracking.buyInteraction('zoom phone numbers', `${this.callingPlanName} - port your numbers - cancel`);
      return this.handleCancel('close validate port number dialog');
    },
  },
  watch: {
    visible: {
      handler(val) {
        !val && this.resetPortNumberForm();
      },
    },
    errorMessage: {
      handler(message) {
        message && this.$tracking.buyInteraction(
          'zoom phone numbers',
          `${this.callingPlanName} - port your numbers - error - ${message}`,
        );
      },
    },
  },
  mounted() {
    this.$refs?.portValidateDialog?.$el?.addEventListener('click', (e) => {
      if (e?.target?.className === 'port-number-verify-fail') {
        this.$tracking.buyInteraction('zoom phone numbers', `${this.callingPlanName} - port your numbers - error - contact sales`);
      }
    });
  },
};
</script>

<style lang="scss">
@import "../style/dialog.m.scss";
@import '@/styles/responsive.scss';

.port-number-validate {
  .zm-button--link {
    color: #0B5CFF;
  }

  &__error-form {
    padding-bottom: 16px;
  }

  .zm-dialog {
    padding: 32px 0px 92px 0px;

    &__header {
      padding-bottom: 24px;
    }
  }

  &__title, &__content, &__footer {
    padding: 0px 32px;
  }

  &__title {
    .zm-icon-warning-outline {
      color: #0B5CFF;
      font-size: 14px;
      margin-left: 4px;
      cursor: pointer;
    }

    .port-number-guarantee-tips {
      font-weight: normal;
    }
  }

  .zm-form {
    &--inline {
      display: flex;
      align-items: center;

      .zm-form-item {
        flex: 1;
        margin-bottom: 0px;

        &__content {
          width: 100%;
        }

        &__error {
          color: #DD004A;
          font-size: 12px;
        }

        &.is-error {
          .zm-input__inner {
            border-color: #DD004A;
          }
        }
      }
    }
  }

  &__input {
    .zm-icon-loading {
      margin-right: 8px;
    }
  }

  &__btn {

  }

  &__content {
    &--verify-error-msg, .zm-alert {
      font-size: 14px;
      font-weight: 450;
      margin-top: 24px;
      line-height: 150%;
    }

    &--max-license-warning {
      color: #815A30;

      .billing-icon-warning {
        width: 16px;
        height: 16px;
        position: relative;

        &::before {
          content: '';
          background-image: url('../../../../assets/Alert.svg');
          width: 16px;
          height: 16px;
          background-repeat: no-repeat;
          background-size: cover;
          position: absolute;
          top: 3px;
          left: 0;
        }
      }

      p {
        margin-left: 28px;
      }
    }

    .zm-alert {
      &.zm-alert--warning {
        .port-number-max-licenses-check {
          margin-left: 5px;
        }
      }

      &.zm-alert--success {
        background: #E2FBEA;
      }

      &.max-phone-number__alert {
        background: #F7F7F8;
        margin-top: 24px;
      }
    }
  }

  &__checked-list {

  }

  &__footer {
    position: absolute;
    bottom: 0px;
    height: 92px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid #E0E0E6;

    .zm-button {
      padding: 10px 24px;
    }

    .zm-button + .zm-button {
      margin-left: 16px;
    }
  }

  @include media(mobile) {
    .zm-dialog {
      &__footer {
        height: 76px;
        padding-top: 0px;

        .port-number-validate {
          &__footer {
            height: 76px;
            padding: 12px 16px 24px;
            display: flex;
            justify-content: center;
            background: white;

            .zm-button.is-round {
              flex: 1;
            }
          }
        }
      }
    }

    &__title, &__content {
      padding-left: 0px;
      padding-right: 0px;
    }

    &__guarantee-tips {
      color: #515079;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */
      padding-bottom: 16px;
    }
  }

  @include media(tablet) {
    .zm-dialog {
      &__body {
        padding-bottom: 100px;
      }
    }
  }
}
</style>
